import React from 'react';

export const useLocalStorage = (
  key: string,
  initialValue: any,
): [value: any, setValue: (value: any) => void] => {
  const [storedValue, setStoredValue] = React.useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item && item !== undefined && item !== null
        ? JSON.parse(item)
        : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });
  const setValue = (value: any) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.log(error);
    }
  };
  return [storedValue, setValue];
};

export const useBeforeUnload = (
  enabled = true,
  message = 'Changes that you made may not be saved',
) => {
  const onbeforeunloadHandler = React.useCallback(
    (event) => {
      if (!enabled) return;
      event.preventDefault();
      if (message) event.returnValue = message;
      return message;
    },
    [enabled, message],
  );

  React.useEffect(() => {
    if (!enabled) return;
    history.pushState(null, '', null);
    window.addEventListener('beforeunload', onbeforeunloadHandler);
    return () => {
      if (!history.state) {
        history.back();
      }
      window.removeEventListener('beforeunload', onbeforeunloadHandler);
    };
  }, [enabled, onbeforeunloadHandler]);
};

const initBeforeUnLoad = (showExitPrompt) => {
  window.onbeforeunload = (event) => {
    if (showExitPrompt) {
      const e = event || window.event;
      e.preventDefault();
      if (e) {
        e.returnValue = '';
      }
      return '';
    }
  };
};

export const useExitPrompt = (bool) => {
  const [showExitPrompt, setShowExitPrompt] = React.useState(bool);

  window.onload = () => {
    initBeforeUnLoad(showExitPrompt);
  };

  React.useEffect(() => {
    initBeforeUnLoad(showExitPrompt);
  }, [showExitPrompt]);

  return [showExitPrompt, setShowExitPrompt];
};

export const useOnClickOutside = (ref: any, handler: any) => {
  React.useEffect(() => {
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};
